// src/services/dashboardService.js
import { getAuthHeader } from "./authService";

/** Institución fija (dato quemado) */
export const INSTITUCION_ID = "16020324-f0ea-4319-905c-5a30618ce9f5";

/** Base URL del backend */
const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/dashboard`;

/* ========== Helpers ========== */
const toJson = (res) => res.json().catch(() => ({}));

const ensureOk = (res, data) => {
  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      (typeof data === "string" ? data : "") ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
};

/**
 * GET - KPIs del sistema para la institución
 * Endpoint: GET /api/dashboard/kpis/:contextInstitutionId
 * @param {string} [institutionId] - ID de la institución (opcional)
 * @returns {Promise<Object>} KPIs
 */
export async function getKpis() {
  const res = await fetch(`${BASE_URL}/kpis/${INSTITUCION_ID}`, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}

/**
 * GET - Conteo de deportistas por posición
 * Endpoint: GET /api/dashboard/deportistas-posicion/:contextInstitutionId
 * Filtros: genero
 * @param {Object} filters
 * @param {string} [filters.genero]
 * @param {string} [institutionId]
 * @returns {Promise<Object>} { data: [...], filtros_aplicados: {...} }
 */
export async function getDeportistasDistribution(
    filters = {}
) {
    const qp = new URLSearchParams();
    if (filters.genero) qp.set("genero", filters.genero);

    const url = `${BASE_URL}/deportistas-posicion/${INSTITUCION_ID}${
        qp.toString() ? `?${qp.toString()}` : ""
    }`;

    const res = await fetch(url, {
        headers: { Accept: "application/json", ...getAuthHeader() },
    });
    const data = await toJson(res);
    ensureOk(res, data);
    return {
        data: data?.data ?? [],
        filtros_aplicados: data?.filtros_aplicados ?? {},
    };
}

/**
 * GET - Asistencias por evento (presencia, ausencias, tardanzas)
 * Endpoint: GET /api/dashboard/asistencias/:contextInstitutionId
 * Filtros: evento_id, fecha_inicio, fecha_fin
 */
export async function getAsistenciasPorEvento(
  filters = {},
  institutionId = INSTITUCION_ID
) {
  const qp = new URLSearchParams();
  if (filters.evento_id) qp.set("evento_id", String(filters.evento_id));
  if (filters.fecha_inicio) qp.set("fecha_inicio", filters.fecha_inicio);
  if (filters.fecha_fin) qp.set("fecha_fin", filters.fecha_fin);

  const url = `${BASE_URL}/asistencias/${institutionId}${
    qp.toString() ? `?${qp.toString()}` : ""
  }`;

  const res = await fetch(url, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return {
    data: data?.data ?? [],
    filtros_aplicados: data?.filtros_aplicados ?? {},
  };
}

/**
 * GET - Asistencias por grupo (presencia, ausencias, tardanzas)
 * Endpoint: GET /api/dashboard/asistencias-grupos/:contextInstitutionId
 * Filtros: evento_id, fecha_inicio, fecha_fin
 * @param {Object} filters
 * @returns {Promise<Array>} Array de objetos con campos: grupo_id, grupo_nombre, categoria, presentes, justificados, ausentes, tardanzas
 */
export async function getAsistenciasPorGrupos(
  filters = {},
  institutionId = INSTITUCION_ID
) {
  const qp = new URLSearchParams();
  if (filters.evento_id) qp.set("evento_id", String(filters.evento_id));
  if (filters.fecha_inicio) qp.set("fecha_inicio", filters.fecha_inicio);
  if (filters.fecha_fin) qp.set("fecha_fin", filters.fecha_fin);

  const url = `${BASE_URL}/asistencias-grupos/${institutionId}${
    qp.toString() ? `?${qp.toString()}` : ""
  }`;

  const res = await fetch(url, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  // Expecting data.data to be an array
  return data?.data ?? [];
}

/**
 * GET - Deportistas agrupados por grupo con conteo total
 * Endpoint: GET /api/dashboard/deportistas-grupo/:contextInstitutionId
 * Query: grupo (string) - id del grupo para filtrar (opcional)
 * Returns: DeportistasByGrupos[] objetos con: { grupo_id, categoria, total_deportistas }
 */
export async function getDeportistasByGrupos(
  filters = {},
  institutionId = INSTITUCION_ID
) {
  const qp = new URLSearchParams();
  if (filters.grupo) qp.set("grupo", String(filters.grupo));
  // compatibilidad por si se pasa grupo_id
  if (filters.grupo_id) qp.set("grupo", String(filters.grupo_id));

  const url = `${BASE_URL}/deportistas-grupo/${institutionId}${
    qp.toString() ? `?${qp.toString()}` : ""
  }`;

  const res = await fetch(url, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? [];
}

/**
 * GET - Próximos eventos
 * Endpoint: GET /api/dashboard/proximos/:contextInstitutionId
 * Filtro: tipo
 */
export async function getProximosEventos(
  filters = {},
  institutionId = INSTITUCION_ID
) {
  const qp = new URLSearchParams();
  if (filters.tipo) qp.set("tipo", filters.tipo);

  const url = `${BASE_URL}/proximos/${institutionId}${
    qp.toString() ? `?${qp.toString()}` : ""
  }`;

  const res = await fetch(url, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? [];
}

const DashboardService = {
  getKpis,
  getDeportistasDistribution,
  getAsistenciasPorEvento,
  getAsistenciasPorGrupos,
  getDeportistasByGrupos,
  getProximosEventos,
};

export default DashboardService;
